import { Link, Outlet, useNavigate } from '@tanstack/react-router'
import {
  LayoutGrid,
  MessagesSquare,
  GraduationCap,
  UserRound,
  Users,
  LogOut,
  Loader2,
  Settings,
} from 'lucide-react'
import { useCurrentUser, useSignOut } from '~/lib/auth'
import { Logo } from '~/components/Logo'
import ThemeToggle from '~/components/ThemeToggle'
import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar'
import { Button } from '~/components/ui/button'
import { Separator } from '~/components/ui/separator'
import type { User } from '~/lib/types'

const NAV = [
  { to: '/dashboard', label: 'Overview', icon: LayoutGrid, exact: true },
  { to: '/conversations', label: 'Conversations', icon: MessagesSquare, exact: false },
  { to: '/courses', label: 'Courses', icon: GraduationCap, exact: false },
  { to: '/students', label: 'Students', icon: UserRound, exact: false },
  { to: '/team', label: 'Team', icon: Users, exact: false },
] as const

export default function AppShell() {
  const { data: user } = useCurrentUser()
  const signOut = useSignOut()
  const navigate = useNavigate()

  const handleSignOut = () =>
    signOut.mutate(undefined, { onSuccess: () => navigate({ to: '/login', replace: true }) })

  return (
    <div className="min-h-screen bg-plane text-ink">
      <div className="flex">
        <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-line px-4 py-6 md:flex">
          <Link to="/dashboard" className="px-2">
            <Logo />
          </Link>

          <nav className="mt-8 flex flex-1 flex-col gap-1">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                activeOptions={{ exact: item.exact }}
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-ink-2 transition hover:bg-surface hover:text-ink"
                activeProps={{ className: 'bg-surface font-medium text-ink shadow-xs' }}
              >
                <item.icon className="size-4 text-faint" aria-hidden="true" />
                {item.label}
              </Link>
            ))}

            {/* The admin panel is a separate Rails-rendered app, so this is a
                plain anchor — routing it through TanStack would 404. Without it
                there is no way to reach the screens that add a course or a
                student except typing the URL. */}
            <a
              href="/admin"
              className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-ink-2 transition hover:bg-surface hover:text-ink"
            >
              <Settings className="size-4 text-faint" aria-hidden="true" />
              Manage
            </a>
          </nav>

          {/* Account block sits at the bottom of the rail: identity, theme, sign out. */}
          <div className="flex flex-col gap-3">
            <Separator />

            <div className="flex items-center gap-2.5 px-1">
              <UserAvatar user={user} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium leading-tight">{user?.name}</p>
                <p className="truncate text-xs text-faint">{user?.email}</p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2">
              <ThemeToggle />
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignOut}
                disabled={signOut.isPending}
                aria-label="Sign out"
                title="Sign out"
              >
                {signOut.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <LogOut className="size-3.5" aria-hidden="true" />
                )}
              </Button>
            </div>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          {/* The rail is hidden below md, so the header carries nav and controls there. */}
          <header className="sticky top-0 z-10 border-b border-line bg-plane/80 backdrop-blur md:hidden">
            <div className="flex items-center justify-between gap-4 px-6 py-3">
              <Link to="/dashboard">
                <Logo />
              </Link>

              <div className="flex items-center gap-2">
                <ThemeToggle />
                <UserAvatar user={user} />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSignOut}
                  disabled={signOut.isPending}
                  aria-label="Sign out"
                >
                  <LogOut className="size-3.5" aria-hidden="true" />
                </Button>
              </div>
            </div>

            <nav className="flex gap-1 overflow-x-auto px-4 pb-2">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  activeOptions={{ exact: item.exact }}
                  className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm whitespace-nowrap text-ink-2 transition hover:bg-surface hover:text-ink"
                  activeProps={{ className: 'bg-surface font-medium text-ink shadow-xs' }}
                >
                  <item.icon className="size-4 text-faint" aria-hidden="true" />
                  {item.label}
                </Link>
              ))}
            </nav>
          </header>

          <main className="px-6 py-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  )
}

function UserAvatar({ user }: { user: User | null | undefined }) {
  return (
    <Avatar className="size-8 border border-line">
      {user?.avatar_url && <AvatarImage src={user.avatar_url} alt="" />}
      <AvatarFallback>{user?.name?.[0]?.toUpperCase() ?? '?'}</AvatarFallback>
    </Avatar>
  )
}
